<script setup lang="ts">
  import {useI18n} from 'vue-i18n';
  import {computed} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpBodyAction from '~/components/cp/CpBodyAction/CpBodyAction.vue';
  import CpAlert from '~/components/cp/CpAlert/CpAlert.vue';
  import CpTextParser from '~/components/cp/CpTextParser/CpTextParser.vue';
  import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
  import CpModal from '~/components/cp/CpModal/CpModal.vue';

  const props = withDefaults(defineProps<{
    show?: boolean,
    showDelete?: boolean,
    predetermined?: boolean,
    withPicture?: boolean,
    wishlistKind?: 'account' | 'oos' | 'standard' | 'eol'
    wishlistProductHideOptions?: boolean
    type: 'list'|'product'|'paypalBa'|'address'|'billing'|'warranties'|'reportUcg'|'profilePicture'|'viewHistory'
  }>(), {
    wishlistKind: 'account',
    wishlistProductHideOptions: false,
  });

  const emit = defineEmits<{
    (e:'update:show', value:boolean): void,
    (e:'delete-history'): void,
    (e:'delete-list'): void,
    (e:'delete-paypal'): void
    (e:'edit-paypal'): void
    (e:'rename-list'): void
    (e:'default-list'): void
    (e:'edit-address'): void
    (e:'edit-billing'): void
    (e:'move-product'): void
    (e:'add-to-another-list'): void
    (e:'see-category'): void
    (e:'delete-product-from-list'): void
    (e:'delete-product-from-saved'): void
    (e:'delete-billing'): void
    (e:'report-comment'): void
    (e:'delete-address'): void
    (e:'delete-warranty-data'): void
    (e:'edit-warranty-data'): void
  }>();
  const value = computed({
    get() {
      return props.show;
    },
    set(value) {
      emit('update:show', value);
    },
  });

  const headerTitle = computed(() => {
    switch (props.type) {
      case 'list':
        return t('listActions');
      case 'product':
        return t('productActions');
      case 'paypalBa':
        return t('paypalActions');
      case 'address':
        return t('addressActions');
      case 'billing':
        return t('billingActions');
      case 'warranties':
        return t('warrantiesActions');
      case 'reportUcg':
        return t('contentActions');
      case 'profilePicture':
        return t('profilePictureActions');
      case 'viewHistory':
        return t('historyActions');
      default:
        return '';
    }
  });

  const deleteHistory = () => {
    value.value = false;
    emit('delete-history');
  };

  const {b, e} = useCpBem('cp-modal-actions');
  const {t} = useI18n();
</script>

<template>
    <div :class="b">
        <CpModal v-model:show="value">
            <CpSectionHeader
                :class="e('title')"
                :title="headerTitle"
                type="h3"
                button="close"
                @header-action="value = false"
            />
            <div v-if="type === 'product'">
                <div v-if="!wishlistProductHideOptions">
                    <CpBodyAction v-if="wishlistKind !== 'eol'"
                        icon="swap_vert"
                        :label="t('moveProduct')"
                        divider
                        @click="emit('move-product')"
                    />
                    <CpBodyAction v-if="wishlistKind === 'account'"
                        icon="favorite"
                        :label="t('addProduct')"
                        divider
                        @click="emit('add-to-another-list')"
                    />
                    <CpBodyAction v-if="wishlistKind === 'oos' "
                        icon="view_module"
                        :label="t('seeCategory')"
                        divider
                        @click="emit('see-category')"
                    />
                </div>
                <CpBodyAction v-if="wishlistKind === 'account'"
                    icon="delete"
                    :label="t('deleteProduct')"
                    divider
                    @click="emit('delete-product-from-list')"
                />
                <CpBodyAction v-if="wishlistKind === 'eol'"
                    icon="delete"
                    :label="t('deleteProductFromSaved')"
                    divider
                    @click="emit('delete-product-from-saved')"
                />
            </div>
            <div v-if="type === 'list'">
                <CpBodyAction
                    v-if="predetermined"
                    icon="grading"
                    :label="t('selectList')"
                    divider
                    data-testid="set-default-list"
                    @click="emit('default-list')"
                />
                <CpBodyAction icon="list_alt"
                    :label="t('changeName')"
                    divider
                    data-testid="rename-list"
                    @click="emit('rename-list')"
                />
                <CpBodyAction
                    v-if="predetermined"
                    icon="delete"
                    :label="t('deleteList')"
                    divider
                    data-testid="delete-list"
                    @click="emit('delete-list')"
                />
            </div>
            <div v-if="type === 'paypalBa'">
                <CpBodyAction icon="edit" :label="t('editAgreement')" divider @click="$emit('edit-paypal')" />
                <CpBodyAction v-if="showDelete"
                    icon="delete"
                    :label="t('deleteAgreement')"
                    divider
                    @click="$emit('delete-paypal')"
                />
            </div>
            <div v-if="type === 'address'">
                <CpBodyAction data-testid="edit-address-option"
                    icon="edit"
                    :label="t('editAddress')"
                    divider
                    @click="emit('edit-address')"
                />
                <CpBodyAction
                    v-if="showDelete"
                    data-testid="delete-address-option"
                    icon="delete"
                    :label="t('deleteAddress')"
                    divider
                    @click="emit('delete-address')"
                />
            </div>
            <div v-if="type === 'billing'">
                <CpBodyAction icon="edit"
                    :label="t('editBilling')"
                    divider
                    data-testid="edit-billing-data"
                    @click="emit('edit-billing')"
                />
                <CpBodyAction
                    v-if="showDelete"
                    icon="delete"
                    :label="t('deleteBilling')"
                    divider
                    data-testid="delete-billing-data"
                    @click="emit('delete-billing')"
                />
            </div>
            <div v-if="type === 'warranties'">
                <CpBodyAction icon="edit" :label="t('editInfo')" divider @click="emit('edit-warranty-data')" />
                <CpBodyAction icon="delete" :label="t('deleteInfo')" divider @click="emit('delete-warranty-data')" />
            </div>
            <div v-if="type === 'reportUcg'">
                <CpBodyAction icon="outlined_flag" :label="t('report')" divider @click="$emit('report-comment')" />
            </div>
            <div v-if="type === 'profilePicture'">
                <CpAlert :action=" false" :close-icon="false" :class="e('alert')">
                    <ul :class="e('list')">
                        <li :class="e('list-item')">
                            <CpTextParser :text="t('imageSize')" />
                        </li>
                        <li :class="e('list-item')">
                            <CpTextParser :text="t('imageFormat')" />
                        </li>
                        <li :class="e('list-item')">
                            <CpTextParser :text="t('imageCut')" />
                        </li>
                    </ul>
                </CpAlert>
                <CpBodyAction icon="add_a_photo" :label="t('takePicture')" divider />
                <CpBodyAction icon="image" :label="t('selectPhoto')" divider />
                <CpBodyAction v-if="withPicture" icon="delete" :label="t('deletePhoto')" divider />
            </div>
            <div v-if="type === 'viewHistory'">
                <CpBodyAction icon="delete" :label="t('deleteHistory')" divider @click="deleteHistory" />
            </div>
        </CpModal>
    </div>
</template>

<style scoped lang="scss">
  @import "assets/style/global.scss";
  .cp-modal-actions {
    &__title {
      border-top: none;
    }
    &__alert {
      margin: 10px;
    }
    &__list {
      padding-left: $cp-gutter;
      list-style-type: none;
      li::before {
        content:"·";
        font-size:20px;
        vertical-align: middle;
        line-height: var(--cp-body-regular-line-height);
        margin-left: -8px;
        margin-right: $cp-gutter-small;
      }
    }
    &__list-item {
      display: flex;
    }
  }
</style>
<i18n locale="es-US">
  listActions: "Acciones de lista"
  productActions: "Acciones de producto"
  paypalActions: "Acciones de acuerdo de pagos con PayPal"
  addressActions: "Acciones de dirección"
  billingActions: "Acciones de facturación"
  warrantiesActions: "Acciones de datos para activación"
  contentActions: "Acciones de contenido"
  profilePictureActions: "Acciones de foto de perfil"
  historyActions: "Acciones de historial"
  moveProduct: "Mover producto a otra lista"
  addProduct: "Agregar producto a otra lista"
  deleteProduct: "Eliminar producto de la lista"
  deleteProductFromSaved: "Eliminar producto de guardados"
  seeCategory: "Ver categoría"
  selectList: "Seleccionar como lista predeterminada"
  changeName: "Cambiar nombre"
  deleteList: "Eliminar lista"
  editAgreement: "Editar acuerdo"
  deleteAgreement: "Eliminar acuerdo"
  editAddress: "Editar dirección"
  deleteAddress: "Eliminar dirección"
  editBilling: "Editar datos de facturación"
  deleteBilling: "Eliminar datos de facturación"
  editInfo: "Editar o completar datos"
  deleteInfo: "Eliminar datos"
  report: "Reportar"
  imageSize: "El tamaño de imagen máximo es *4MB*."
  imageFormat: "La imagen debe estar en formato *jpg*, *png* o *jpeg*."
  imageCut: "Tu imagen será recortada a *400x400 pixeles*."
  takePicture: "Tomar foto"
  selectPhoto: "Seleccionar foto"
  deletePhoto: "Eliminar foto"
  deleteHistory: "Eliminar historial"
</i18n>
